import React, { useEffect, useState } from 'react';
import { listThreads, createThread } from './api';
import { COPY } from './copy';
import MessageList from './MessageList';
import RequestPromptModal from './RequestPromptModal';

// GP-side panel on a CLOSED issue (story #26555).
// - If no GP→Specialist thread exists, render the instructive section + button.
// - Once the GP has opened one, render the read-only "ADDITIONAL INFORMATION
//   YOU REQUESTED" panel with prompt + timestamp. Button is hidden — one-shot
//   per consult.
const GpClosedIssueRequestSection = ({ issue }) => {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  const refresh = async () => {
    try {
      const data = await listThreads(issue.id);
      const gpThread = (data || []).find((t) => t.direction === 'gp_to_specialist');
      setThread(gpThread || null);
      setLoadFailed(false);
    } catch (err) {
      // 403/404 from auth → no access; hide the section entirely.
      // Other errors (network/5xx) → suppress the prompt button so we don't
      // mislead the GP into a duplicate-create that 422s; show a tiny notice.
      if (err.response?.status === 403 || err.response?.status === 404) {
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [issue.id]);

  const handleSubmit = async ({ body }) => {
    const created = await createThread(issue.id, { body });
    setThread(created);
  };

  if (loading) return null;

  if (loadFailed) {
    return (
      <div className="bg-white rounded shadow mb-4 p-4">
        <p className="text-sm text-slate-600">
          Could not load request status. Please refresh the page.
        </p>
      </div>
    );
  }

  if (thread) {
    const gpPrompt = thread.messages.find((m) => m.author?.role === 'gp');
    const specialistResponse = thread.messages.find((m) => m.author?.role === 'specialist');

    return (
      <div className="mb-4">
        <div className="bg-white rounded shadow mb-4">
          <div
            className="font-semibold uppercase text-sm text-white px-3 py-2 rounded-t"
            style={{ backgroundColor: COPY.hex.alertRed }}
          >
            {COPY.sections.additionalInformationYouRequested}
          </div>
          <div className="border border-t-0 border-gray-200 rounded-b p-3 bg-slate-50">
            <MessageList messages={gpPrompt ? [gpPrompt] : []} />
          </div>
        </div>

        {specialistResponse && (
          <div className="bg-white rounded shadow mb-4">
            <div
              className="font-semibold uppercase text-sm text-white px-3 py-2 rounded-t"
              style={{ backgroundColor: COPY.hex.closedBlue }}
            >
              {COPY.sections.additionalInformationProvided}
            </div>
            <div className="border border-t-0 border-gray-200 rounded-b p-3 bg-slate-50">
              <MessageList messages={[specialistResponse]} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded shadow mb-4 p-4">
      <p className="text-sm text-slate-600 mb-2">{COPY.gpRequestSection.helpText}</p>
      <p className="text-sm text-slate-700 font-semibold mb-3">{COPY.gpRequestSection.constraintEmphasis}</p>
      <button
        type="button"
        onClick={() => setPromptOpen(true)}
        className="btn font-semibold text-white px-4 py-2 rounded uppercase"
        style={{ backgroundColor: COPY.hex.alertRed }}
      >
        {COPY.buttons.openRequest}
      </button>
      <RequestPromptModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
        onSubmit={handleSubmit}
        description={COPY.modal.descriptionForGp}
      />
    </div>
  );
};

export default GpClosedIssueRequestSection;
